import React, { useCallback, useEffect, useState } from 'react';
import platformApi, { platformErrorMessage } from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../utils/billingAdmin';
import {
  LEAD_STATUSES,
  PERIOD_OPTIONS,
  businessTypeLabel,
  formatPhone,
  leadStatusMeta,
  trackedShare,
  whatsappLink,
} from '../utils/leadsAdmin';

function Kpi({ label, value, hint }) {
  return (
    <div className="stat-card" style={{ flexDirection: 'column', gap: '0.25rem' }}>
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value" style={{ fontSize: '1.5rem' }}>{value}</div>
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );
}

function SourceCell({ row }) {
  return (
    <>
      <div>{row.source}</div>
      {row.campaign && <div className="form-hint">Campaign: {row.campaign}</div>}
      {row.lastSource && row.lastSource !== row.source && (
        <div className="form-hint">Last visit: {row.lastSource}</div>
      )}
    </>
  );
}

function LeadStatusSelect({ lead, saving, onChange }) {
  const meta = leadStatusMeta(lead.status);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
      <span className={`badge badge--${meta.badge}`}>{meta.label}</span>
      <select
        className="form-select"
        value={lead.status}
        disabled={saving}
        onChange={(e) => onChange(lead, e.target.value)}
        aria-label={`Follow-up status for ${lead.name || 'lead'}`}
      >
        {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>
    </div>
  );
}

function Leads() {
  const toast = useToast();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await platformApi.get('/platform/acquisition', { params: { days } });
      setData(response.data?.data || null);
    } catch (err) {
      setError(platformErrorMessage(err, 'Failed to load leads'));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (lead, status) => {
    if (status === lead.status) return;
    setSavingId(lead.id);
    try {
      const response = await platformApi.patch(`/platform/acquisition/leads/${lead.id}`, { status });
      const saved = response.data?.data || { status };
      setData((current) => {
        if (!current) return current;
        const demoRequests = current.demoRequests.map((row) => (
          row.id === lead.id ? { ...row, status: saved.status, statusUpdatedAt: saved.statusUpdatedAt } : row
        ));
        return {
          ...current,
          demoRequests,
          summary: { ...current.summary, demoRequestsNew: demoRequests.filter((row) => row.status === 'new').length },
        };
      });
      toast.success(`Marked ${lead.name || 'lead'} as ${leadStatusMeta(saved.status).label.toLowerCase()}`);
    } catch (err) {
      toast.error(platformErrorMessage(err, 'Could not update the lead'));
    } finally {
      setSavingId(null);
    }
  };

  if (loading && !data) return <LoadingScreen message="Loading leads..." />;

  const summary = data?.summary;

  return (
    <div className="page">
      <PageHeader
        title="Leads & sources"
        description="Demo requests from inaiy.com and new workspace sign-ups, grouped by how they first found us (UTM tags, ad click IDs or the referring site)."
        action={(
          <div className="btn-group">
            <select className="form-select" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
              {PERIOD_OPTIONS.map((n) => <option key={n} value={n}>Last {n} days</option>)}
            </select>
            <button type="button" className="btn btn--secondary" onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        )}
      />

      {error && <div className="alert alert--error">{error}</div>}

      {data && (
        <>
          <div className="stats-grid">
            <Kpi label="Demo requests" value={summary.demoRequests} hint={`${summary.demoRequestsNew} not contacted yet`} />
            <Kpi label="Sign-ups" value={summary.signups} hint="New customer workspaces" />
            <Kpi label="Sign-ups with a source" value={trackedShare(summary.signupsWithSource, summary.signups)} hint="Others arrived without tracking" />
            <Kpi label="Sign-ups now paying" value={summary.paying} hint="Active paid plan" />
          </div>

          {data.truncated && (
            <p className="form-hint" style={{ marginBottom: '1rem' }}>Showing the latest 500 rows of each list. Pick a shorter period to see everything.</p>
          )}

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card__body">
              <h3 style={{ marginTop: 0 }}>By source</h3>
              {data.sources.length === 0 ? (
                <p className="form-hint">No sign-ups or demo requests in this period.</p>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead><tr><th>Source</th><th>Demo requests</th><th>Sign-ups</th><th>Paying</th></tr></thead>
                    <tbody>
                      {data.sources.map((s) => (
                        <tr key={s.source}>
                          <td>{s.source}</td>
                          <td>{s.demoRequests}</td>
                          <td>{s.signups}</td>
                          <td>{s.paying}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card__body">
              <h3 style={{ marginTop: 0 }}>Demo requests</h3>
              {data.demoRequests.length === 0 ? (
                <EmptyState title="No demo requests yet" description="Requests from inaiy.com/demo appear here and are also emailed to sales." />
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr><th>Received</th><th>Contact</th><th>Business</th><th>Message</th><th>Source</th><th>Follow-up</th></tr>
                    </thead>
                    <tbody>
                      {data.demoRequests.map((lead) => {
                        const wa = whatsappLink(lead.phone);
                        return (
                          <tr key={lead.id}>
                            <td>
                              {formatDate(lead.createdAt, { withTime: true })}
                              {lead.submissions > 1 && <div className="form-hint">Submitted {lead.submissions}×</div>}
                            </td>
                            <td>
                              <strong>{lead.name || '—'}</strong>
                              <div>{wa ? <a href={wa} target="_blank" rel="noopener noreferrer">{formatPhone(lead.phone)}</a> : formatPhone(lead.phone)}</div>
                              {lead.email && <div><a href={`mailto:${lead.email}`}>{lead.email}</a></div>}
                            </td>
                            <td>
                              <div>{lead.company || '—'}</div>
                              {(lead.businessType || lead.teamSize) && (
                                <div className="form-hint">
                                  {[businessTypeLabel(lead.businessType), lead.teamSize && `${lead.teamSize} people`].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>
                            <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{lead.message || '—'}</td>
                            <td><SourceCell row={lead} /></td>
                            <td>
                              <LeadStatusSelect lead={lead} saving={savingId === lead.id} onChange={changeStatus} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card__body">
              <h3 style={{ marginTop: 0 }}>Sign-ups</h3>
              {data.signups.length === 0 ? (
                <EmptyState title="No sign-ups in this period" />
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead><tr><th>Signed up</th><th>Workspace</th><th>Source</th><th>Landing page</th><th>Plan</th></tr></thead>
                    <tbody>
                      {data.signups.map((row) => (
                        <tr key={row.tenantId}>
                          <td>{formatDate(row.createdAt, { withTime: true })}</td>
                          <td>
                            <strong>{row.name || '—'}</strong>
                            <div className="form-hint">
                              {row.subdomain}
                              {row.viaPartner ? ' · via partner' : ''}
                            </div>
                          </td>
                          <td><SourceCell row={row} /></td>
                          <td>{row.landingPage || '—'}</td>
                          <td>
                            {row.plan ? (
                              <>
                                <span className={`badge badge--${row.plan.paying ? 'active' : 'inactive'}`}>
                                  {row.plan.paying ? 'Paying' : (row.plan.status || 'No plan')}
                                </span>
                                <div className="form-hint">{row.plan.planName || '—'}</div>
                              </>
                            ) : <span className="form-hint">No subscription</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Leads;
